"use client";

import { cn } from "@guru/shared/utils/cn";
import { Button } from "../button";
import { useConsent } from "./consent-context";

export interface CookieBannerCopy {
  body: string;
  acceptAll: string;
  essentialOnly: string;
  customise: string;
}

export interface CookieBannerProps {
  copy: CookieBannerCopy;
  className?: string;
}

/**
 * First-visit consent banner. Domain-agnostic — all copy is supplied by the
 * consuming app. Hidden once a decision exists or when GPC is signalled.
 * Accept and Reject use equal-prominence styling (no dark patterns).
 */
export function CookieBanner({ copy, className }: CookieBannerProps) {
  const { hasDecided, gpc, acceptAll, rejectAll, openPreferences } = useConsent();

  if (hasDecided || gpc) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className={cn(
        "fixed inset-x-0 bottom-0 z-[1000] border-t border-border bg-background p-4 shadow-lg",
        className
      )}
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-2xl text-sm text-muted-foreground">{copy.body}</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="default" size="sm" onClick={acceptAll}>
            {copy.acceptAll}
          </Button>
          <Button variant="default" size="sm" onClick={rejectAll}>
            {copy.essentialOnly}
          </Button>
          <Button variant="outline" size="sm" onClick={openPreferences}>
            {copy.customise}
          </Button>
        </div>
      </div>
    </div>
  );
}
